import { useState } from "react";
import { useAffiliateAuth } from "@/lib/affiliateAuth";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

// Sticky referral-link strip shown on every Assets sub-page. The link
// is what affiliates spend their day copy/pasting into content, so it
// needs to be one tap away no matter where they are in the portal.
//
// Layout (desktop): [eyebrow] [link box · COPY] [TEST YOUR LINK →]
// Layout (mobile, <640px): stacked, link first, two buttons row below.
export default function ReferralLinkBar() {
  const { profile } = useAffiliateAuth();
  const [copied, setCopied] = useState(false);
  // The BE attaches the affiliate code under either `code` (shorthand
  // returned by /dashboard, /auth/login, /apply) or `affiliateCode` (raw
  // field returned by /profile). Read both so the bar lights up
  // regardless of which endpoint hydrated the profile snapshot.
  const code = profile?.code || profile?.affiliateCode;
  const link =
    profile?.links?.primaryLink ||
    (code ? `https://voya9.com/?aff=${code}` : "");
  const isReady = !!link;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1300);
    } catch {
      toast("Couldn't auto-copy. Long-press the link to select.", "info");
    }
  };

  const testLink = () => {
    // The link itself sets the affiliate cookie + redirects to landing,
    // so opening it in a new tab gives the affiliate exactly what their
    // followers see — including the reduced consultation fee at checkout.
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid="affiliate-referral-bar"
      style={{
        background: C.ink,
        color: C.cream,
        padding: "14px 24px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 14,
        position: "sticky",
        top: 0,
        zIndex: 25,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: C.peach,
          whiteSpace: "nowrap",
        }}
      >
        ★ YOUR LINK
      </div>
      <div
        data-testid="affiliate-referral-link"
        style={{
          flex: 1,
          minWidth: 200,
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 13,
          padding: "8px 12px",
          background: "rgba(250,246,240,0.06)",
          border: `1px solid rgba(250,246,240,0.15)`,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: isReady ? C.cream : "rgba(250,246,240,0.5)",
          fontStyle: isReady ? "normal" : "italic",
        }}
      >
        {isReady ? link : "Your referral link will appear here once setup is complete."}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={copy}
          disabled={!isReady}
          data-testid="affiliate-referral-copy"
          style={{
            padding: "9px 14px",
            background: !isReady ? "rgba(250,246,240,0.15)" : copied ? C.sageDeep : C.peach,
            color: !isReady ? "rgba(250,246,240,0.5)" : copied ? C.cream : C.ink,
            border: 0,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.22em",
            cursor: !isReady ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            transition: "background 120ms, color 120ms",
          }}
        >
          {copied ? "COPIED ✓" : "COPY"}
        </button>
        <button
          type="button"
          onClick={testLink}
          disabled={!isReady}
          data-testid="affiliate-referral-test"
          style={{
            padding: "9px 14px",
            background: "transparent",
            color: !isReady ? "rgba(250,246,240,0.4)" : C.cream,
            border: `1px solid rgba(250,246,240,${!isReady ? 0.2 : 0.4})`,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.22em",
            cursor: !isReady ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          TEST LINK ↗
        </button>
      </div>
      <div
        data-testid="affiliate-referral-perk"
        style={{
          width: "100%",
          fontSize: 11,
          color: "rgba(250,246,240,0.7)",
          letterSpacing: "0.02em",
        }}
      >
        <span style={{ color: C.peach, fontWeight: 800, letterSpacing: "0.1em" }}>★ FOLLOWER PERK</span>
        <span style={{ marginLeft: 8 }}>
          Anyone who lands on VOYA9 via your link pays{" "}
          <strong style={{ color: C.peach }}>$25</strong> for their first consult instead of $49 (choice-led path).
        </span>
      </div>
    </div>
  );
}
